import { createContext, useContext } from "react";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../context/API_Client";
import CheckOut from "./checkOut";
import { kpiCalls } from "../KPICalls";
import { customToast } from "../../../reusableComponents/customToaster/customToast";
import { PieChartPaymentMethodSplitSchema } from "./subheaders/paymentMethodSplit/PieChartPaymentMethodSplitSchema";
import {
  getCalendarIntervalForGraph,
  getCustomDateRange,
  getUTCDayRange,
  getUTCDayRangePercentage,
  isEmpty,
} from "../../utils/utils";
import { trafficByDevicesSchema } from "../paymentLinkAnalytics/trafficByDevicesSchema";

const ChekoutAnalyticsContext = createContext();

export const ChekoutAnalyticsProvider = ({
  selectedBusinessUnit,
  selectedDropdownValue,
  selectedFilterCurrency,
  refresh,
  singleStartDate,
  startDate,
  endDate,
}) => {
  const {
    getAllVisualizationData,
    getAllUiAnalyticsEvent,
    getAllPaymentMethods,
  } = useAuth();
  const [conversionRate, setConversionRate] = useState({});
  const [averageOrderValue, setAverageOrderValue] = useState({});
  const [cardAbandonmentRate, setCardAbandonmentRate] = useState({});
  const [checkoutCompletionTime, setCheckoutCompletionTime] = useState();
  const [paymentMethodSplitData, setPaymentMethodSplitData] = useState();
  const [checkoutFunnelAnalysis, setCheckoutFunnelAnalysis] = useState();
  const [checkoutFunnelData, setCheckoutFunnelData] = useState();

  const [uiAnalyticsEventData, setUiAnalyticsEventData] = useState({});
  const [
    paymentCategoryAndAvgCompletionTime,
    setPaymentCategoryAndAvgCompletionTime,
  ] = useState();

  const [trafficData, setTrafficData] = useState();

  const resetKips = () => {
    setCheckoutCompletionTime()
    setCardAbandonmentRate({})
    setAverageOrderValue({})
    setConversionRate({})
    setPaymentMethodSplitData()
    setCheckoutFunnelAnalysis()
    setCheckoutFunnelData()
    setPaymentCategoryAndAvgCompletionTime()
    setTrafficData()

  }


  // KPI calls

  useEffect(() => {
    if (
      (selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency) ||
      singleStartDate ||
      (startDate && endDate)
    ) {
      if (
        selectedDropdownValue === "customDate" &&
        !singleStartDate &&
        (!startDate || !endDate)
      ) {
        return;
      }
      resetKips()
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "conversion_percent_agg_report",
          range: "today",
          offset: 0,
          title: "Checkout Conversion Rate",
          fieldName: "conversionValuePercent",
          localeFieldName: "conversionValuePercent",
          attributeFilters: {
            legalEntity: selectedBusinessUnit,
            "caller.callerType": "CHECKOUT",
          },
          advancedAttributeFilters: [
            {
              attrName: "currencyCode",
              operator: "eqOrNotExists",
              values: [selectedFilterCurrency],
            },
          ],
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total percentage of users who click the pay now on the checkout",
        },
        (data) => setConversionRate(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "average_order_value_report",
          range: "today",
          offset: 0,
          title: "Average Order Value",
          fieldName: "avgOrderAmount",
          attributeFilters: {
            operationStatus: "Approved",
            operationName: ["Authorisation", "Sale"],
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
            "caller.callerType": "CHECKOUT",
          },
          localeFieldName: "avgOrderAmountLocaleFull",
          // localeFieldName: "avgOrderAmountLocale",
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total order value divided by total number of orders received from checkout",
        },
        (data) => setAverageOrderValue(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "checkout_transaction_conversion_rate_report",
          range: "today",
          offset: 0,
          title: "Transaction Conversion Rate",
          fieldName: "conversionValuePercent",
          localeFieldName: "conversionValuePercent",
          attributeFilters: {
            legalEntity: selectedBusinessUnit,
            "caller.callerType": "CHECKOUT",
          },
          advancedAttributeFilters: [
            {
              attrName: "currencyCode",
              operator: "eqOrNotExists",
              values: [selectedFilterCurrency],
            },
          ],
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total percentage of successful transactions after the user is redirected from the checkout",
        },
        (data) => setCardAbandonmentRate(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
      kpiCalls(
        getAllVisualizationData,
        selectedDropdownValue,
        {
          url: "ui_analytics_average_user_time_report",
          range: "today",
          offset: 0,
          title: "Checkout Completion Time",
          fieldName: "averageUserDuration",
          attributeFilters: {
            latestEvent: ["CHECKOUT_LOADED", "PAYMENT_INITIATED"],
            legalEntity: selectedBusinessUnit,
            sessionType: "CHECKOUT",
          },
          advancedAttributeFilters: [
            {
              attrName: "currencyCode",
              operator: "eqOrNotExists",
              values: [selectedFilterCurrency],
            },
          ],
          localeFieldName: "averageUserDurationLocale",
          singleStartDate: singleStartDate,
          startDate: startDate,
          endDate: endDate,
          tooltipContent:
            "Total time taken by the user in the completion of checkout",
        },
        (data) => setCheckoutCompletionTime(data),
        (errMsg) => customToast("error", "high", errMsg)
      );
    }
  }, [
    selectedBusinessUnit,
    selectedDropdownValue,
    selectedFilterCurrency,
    refresh,
    singleStartDate,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    getAllUiAnalyticsEvent(
      function (data) {
        setUiAnalyticsEventData(data);
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
  }, []);

  // Checkout Funnel Analysis

  const funnelApiCall = (
    attributeFilters,
    advancedAttributeFilters,
    success
  ) => {
    if (
      selectedDropdownValue === "customDate" &&
      !singleStartDate &&
      (!startDate || !endDate)
    ) {
      return;
    }

    const dateRange =
      selectedDropdownValue === "customDate"
        ? getCustomDateRange(singleStartDate, startDate, endDate)
        : getUTCDayRange(selectedDropdownValue);
    const anotherDateRange =
      selectedDropdownValue === "customDate"
        ? getCustomDateRange(singleStartDate, startDate, endDate)
        : getUTCDayRangePercentage(selectedDropdownValue);

    getAllVisualizationData(
      "ui_analytics_count_report",
      {
        ...dateRange,
        attributeFilters: attributeFilters,
        advancedAttributeFilters: advancedAttributeFilters,
      },
      function (data) {
        const updatedFunnelAnalysis = data?.points.map((item) => {
          const eventName = item?.eventName;
          if (uiAnalyticsEventData[eventName]) {
            return { ...item, eventName: uiAnalyticsEventData[eventName] };
          }
          return item;
        });
        getAllVisualizationData(
          "ui_analytics_count_report",
          {
            ...anotherDateRange,
            attributeFilters: attributeFilters,
            advancedAttributeFilters: advancedAttributeFilters,
          },
          function (data) {
            const percenatgeAnalysis = data?.points.map((item) => {
              const eventName = item?.eventName;
              if (uiAnalyticsEventData[eventName]) {
                return { ...item, eventName: uiAnalyticsEventData[eventName] };
              }
              return item;
            });

            const updatedFunnelData = updatedFunnelAnalysis.map((item) => {
              const matchingPercentageEvent = percenatgeAnalysis?.reduce(
                (matchingEvent, percentageItem) => {
                  if (item?.eventName === percentageItem?.eventName) {
                    return percentageItem;
                  }
                  return matchingEvent;
                },
                null
              );

              let changePercentage = 0;

              if (matchingPercentageEvent) {
                changePercentage =
                  ((item.eventNameCount -
                    matchingPercentageEvent.eventNameCount) /
                    item.eventNameCount) *
                  100;

                const absoluteChangePercentage = Math.abs(changePercentage);
                return {
                  ...item,
                  changePercentage,
                  absoluteChangePercentage,
                };
              }
              const absoluteChangePercentage = Math.abs(changePercentage);
              return { ...item, changePercentage, absoluteChangePercentage };
            });

            success({ updatedFunnelAnalysis, updatedFunnelData });
          },
          function (errorMsg) {
            customToast("error", "high", errorMsg);
          }
        );
      },
      function (errorMsg) {
        customToast("error", "high", errorMsg);
      }
    );
  };

  useEffect(() => {
    if (
      singleStartDate ||
      (startDate && endDate) ||
      (selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency &&
        uiAnalyticsEventData)
    ) {
      const attributeFilters = {
        legalEntity: selectedBusinessUnit,
        uiEvent: [
          "CHECKOUT_LOADED",
          // "USER_AUTHENTICATED",
          "ADDRESS_UPDATED",
          "PAYMENT_CATEGORY_SELECTED",
          "PAYMENT_METHOD_SELECTED",
          "PAYMENT_INITIATED",
        ],
        "caller.callerType": "CHECKOUT",
      };
      const advancedAttributeFilters = [
        {
          attrName: "currencyCode",
          operator: "eqOrNotExists",
          values: [selectedFilterCurrency],
        },
      ];
      funnelApiCall(attributeFilters, advancedAttributeFilters, (data) => {
        setCheckoutFunnelAnalysis(data?.updatedFunnelAnalysis);
        setCheckoutFunnelData(data?.updatedFunnelData);
      });
    }
  }, [
    selectedDropdownValue,
    uiAnalyticsEventData,
    refresh,
    selectedBusinessUnit,
    selectedFilterCurrency,
    singleStartDate,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (
      selectedDropdownValue === "customDate" &&
      !singleStartDate &&
      (!startDate || !endDate)
    ) {
      return;
    }

    const dateRange =
      selectedDropdownValue === "customDate"
        ? getCustomDateRange(singleStartDate, startDate, endDate)
        : getUTCDayRange(selectedDropdownValue);
    if (
      singleStartDate ||
      (startDate && endDate) ||
      (selectedDropdownValue &&
        selectedDropdownValue &&
        selectedBusinessUnit?.length > 0 &&
        selectedFilterCurrency)
    ) {
      getAllVisualizationData(
        "checkout_payment_method_gmv_count_aggs_report",
        {
          ...dateRange,
          attributeFilters: {
            operationStatus: "Approved",
            operationName: "Authorisation",
            legalEntity: selectedBusinessUnit,
            currencyCode: selectedFilterCurrency,
            "caller.callerType": "CHECKOUT",
          },
        },
        function (data) {
          setPaymentMethodSplitData(
            PieChartPaymentMethodSplitSchema(
              data?.points,
              data?.metrics,
              "value"
            )
          );
        },
        function (errorMsg) {
          customToast("error", "high", errorMsg);
        }
      );
      getAllVisualizationData(
        "ui_analytics_payment_category_user_time_report",
        {
          ...dateRange,
          attributeFilters: {
            legalEntity: selectedBusinessUnit,
            sessionType: "CHECKOUT",
          },
          advancedAttributeFilters: [
            {
              attrName: "currencyCode",
              operator: "eqOrNotExists",
              values: [selectedFilterCurrency],
            },
          ],
        },
        function (data) {
          getAllPaymentMethods(
            (paymentCategoryData) => {
              for (let i = 0; i < data?.points?.length; i++) {
                let point = data?.points[i];
                let match = paymentCategoryData?.find(
                  (ele) => ele?.type === point?.paymentCategory
                );
                if (match) {
                  point.paymentCategoryLocale = match?.typeTitle;
                } else {
                  point.paymentCategoryLocale = point.paymentCategory;
                }
                let newPoint = {
                  ...point,
                  averageUserDurationLocale_sort: point?.averageUserDuration,
                };
                data.points[i] = newPoint;
              }
              setPaymentCategoryAndAvgCompletionTime(data?.points);
            },
            (errorMsg) => {
              customToast("error", "high", errorMsg);
            }
          );
        },
        function (errorMsg) {
          customToast("error", "high", errorMsg);
        }
      );

      const calendarInterval = getCalendarIntervalForGraph(
        selectedDropdownValue
      );
      getAllVisualizationData(
        "checkout_traffic_percent_device_type_report",
        {
          ...dateRange,
          calendarInterval: calendarInterval,
          attributeFilters: {
            legalEntity: selectedBusinessUnit,
            sessionType: "CHECKOUT",
          },
          advancedAttributeFilters: [
            {
              attrName: "currencyCode",
              operator: "eqOrNotExists",
              values: [selectedFilterCurrency],
            },
          ],
        },
        function (data) {
          setTrafficData(trafficByDevicesSchema(data?.points, data?.metrics));
        },
        function (errorMsg) {
          customToast("error", "high", errorMsg);
        }
      );
    }
  }, [
    selectedDropdownValue,
    refresh,
    selectedBusinessUnit,
    selectedFilterCurrency,
    singleStartDate,
    startDate,
    endDate,
  ]);

  return (
    <ChekoutAnalyticsContext.Provider
      value={{
        checkoutCompletionTime,
        cardAbandonmentRate,
        averageOrderValue,
        conversionRate,
        uiAnalyticsEventData,
        paymentMethodSplitData,
        checkoutFunnelAnalysis,
        checkoutFunnelData,
        paymentCategoryAndAvgCompletionTime,
        trafficData,
        singleStartDate,
        startDate,
        endDate,
      }}
    >
      <CheckOut />
    </ChekoutAnalyticsContext.Provider>
  );
};

export const CheckoutAnalyticsStates = () => {
  return useContext(ChekoutAnalyticsContext);
};
